import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lock, Mail, Loader2, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Bienvenido al panel");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(" ")
          : "No se pudo iniciar sesión";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-slate-950 flex flex-col items-center justify-center px-4 py-10 tech-grid"
    >
      <div className="absolute top-6 left-6">
        <Link
          to="/"
          data-testid="login-back-home"
          className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-sky-400 transition-colors"
        >
          <ArrowLeft size={14} /> Volver al sitio
        </Link>
      </div>

      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900/80 backdrop-blur p-8">
        <div className="flex flex-col items-center mb-7">
          <Logo size="lg" />
          <p className="font-mono text-xs text-orange-400 mt-3">
            // panel.administrativo
          </p>
        </div>

        <h1 className="text-2xl font-semibold text-white text-center">
          Acceso restringido
        </h1>
        <p className="text-sm text-slate-400 text-center mt-2">
          Ingresa con tus credenciales de administrador.
        </p>

        <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="admin-login-form">
          <div>
            <label className="font-mono text-xs text-sky-400">// correo</label>
            <div className="relative mt-2">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-input-email"
                placeholder="isai.coria@divelopers.digital"
                autoComplete="email"
                className="w-full pl-9 pr-4 py-3 rounded-lg bg-slate-950/60 border border-slate-800 text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500/60 focus:ring-2 focus:ring-sky-500/20"
              />
            </div>
          </div>

          <div>
            <label className="font-mono text-xs text-sky-400">// contraseña</label>
            <div className="relative mt-2">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-input-password"
                placeholder="••••••••"
                autoComplete="current-password"
                className="w-full pl-9 pr-4 py-3 rounded-lg bg-slate-950/60 border border-slate-800 text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500/60 focus:ring-2 focus:ring-orange-500/20"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg font-semibold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_0_25px_rgba(249,115,22,0.35)]"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Ingresando...
              </>
            ) : (
              "Entrar al panel"
            )}
          </button>
        </form>
      </div>

      <p className="font-mono text-[11px] text-slate-600 mt-6">
        // solo personal autorizado
      </p>
    </div>
  );
}
